import React, { useMemo, useState, useEffect, useRef } from 'react';
import { get, pick, set } from 'lodash';
import classNames from 'classnames';

import type { IMetadataEndpoint } from '@broadleaf/admin-components/dist/types/metadata';
import useTranslateMode from '@broadleaf/admin-components/dist/form/hooks/useTranslateMode';
import { Formik } from 'formik';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  ContextParameters,
  getInheritedContextState
} from '@broadleaf/admin-components/dist/tracking/utils/ContextRequest';
import { FieldError } from '@broadleaf/admin-components/dist/form';
import { hooks } from '@broadleaf/admin-components';
import { publishRowEdit } from 'app/common/utils/rowEdits';
const { useEventCallback } = hooks;

/**
 * Renders a row value that can be edited in place. Only the field itself and the configured
 * `updateFields` are submitted, so the update endpoint should apply partial updates (PATCH) to
 * avoid overwriting other inline-editable columns in the same row.
 */
const InlineEditField: React.FC<InlineEditFieldProps> = props => {
  const {
    disabled,
    name,
    updateEndpoint,
    updateFields,
    readOnly,
    row,
    contextParams
  } = props;

  const rowValue = get(row, name) ?? '';
  // the grid's row isn't refreshed after a save, so track the saved value locally
  const [savedValue, setSavedValue] = useState(rowValue);
  useEffect(() => setSavedValue(rowValue), [rowValue]);

  const initialValues = useMemo(() => {
    const values = pick({ ...row, ...contextParams }, updateFields || []);
    return set(values, name, savedValue);
  }, [row, contextParams, updateFields, name, savedValue]);

  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSubmit = useSubmitUpdate({
    updateEndpoint,
    contextParams,
    fieldName: name,
    setError,
    setIsEditing,
    onSaved: value => {
      setSavedValue(value ?? '');
      // let the row form action see the saved value
      publishRowEdit(row, { [name]: value });
    }
  });

  const translateMode = useTranslateMode();

  const handleCancel = () => {
    setIsEditing(false);
    setError(undefined);
  };

  if (!isEditing) {
    return (
      <>
        <div
          className="inline-edit-display tw-rounded tw-px-2 tw-py-1 tw-transition-colors hover:tw-bg-gray-100"
          onClick={() => !disabled && !readOnly && setIsEditing(true)}
          style={{
            cursor: disabled || readOnly ? 'default' : 'pointer',
            minWidth: '40px',
            minHeight: '1.5em'
          }}
        >
          {savedValue || <span className="tw-text-gray-400">N/A</span>}
        </div>
        {error ? <FieldError error={error} /> : null}
      </>
    );
  }

  return (
    <>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {formik => {
          const value = get(formik.values, name) ?? '';
          const submitIfChanged = () => {
            if (formik.isSubmitting) {
              return;
            }
            if (value !== savedValue) {
              formik.submitForm();
            } else {
              handleCancel();
            }
          };
          return (
            <input
              ref={inputRef}
              className={classNames('form-control', 'inline-edit-input', {
                'is-invalid': !!error
              })}
              disabled={
                translateMode?.isActive ||
                disabled ||
                readOnly ||
                formik.isSubmitting
              }
              name={name}
              onChange={formik.handleChange}
              onBlur={submitIfChanged}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  submitIfChanged();
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              value={value}
              autoComplete="off"
            />
          );
        }}
      </Formik>
      {error ? <FieldError error={error} /> : null}
    </>
  );
};

function useSubmitUpdate({
  updateEndpoint,
  contextParams,
  fieldName,
  setError,
  setIsEditing,
  onSaved
}) {
  return useEventCallback(
    async (values, formik) => {
      try {
        formik.setSubmitting(true);
        const response = await request(
          { ...updateEndpoint, data: values },
          {
            ...contextParams,
            [ContextParameters.CONTEXT_STATE]: getInheritedContextState(values),
            ...values
          }
        );
        // prefer the persisted value, since the server may normalize it (e.g. blank -> null)
        const persisted = get(
          response,
          ['data', fieldName],
          get(values, fieldName)
        );
        onSaved(persisted);
        formik.setSubmitting(false);
        setIsEditing(false);
        setError(undefined);
      } catch (err) {
        formik.setFieldError(fieldName, err.message);
        setError(err.message);
        formik.setSubmitting(false);
      }
    },
    [updateEndpoint, contextParams, fieldName]
  );
}

InlineEditField.defaultProps = {
  disabled: false,
  readOnly: false,
  updateFields: [],
  fieldMappings: {}
};

export interface InlineEditFieldProps {
  disabled?: boolean;
  readOnly: boolean;
  updateEndpoint: IMetadataEndpoint;
  /** The name of the row field being edited. */
  name: string;
  /** Additional row fields submitted with the edited value, e.g. ids used in the endpoint uri. */
  updateFields: string[];
  row: any;
  contextParams?: any;
  fieldMappings?: any;
  rowId: string;
}

export { InlineEditField };
